<script lang="ts">
	import { type Component, type Snippet } from "svelte"
	import { NEW_ROUTES, ROUTES } from "../../routes/Routes"
	import {
		CalendarIcon,
		ImageIcon,
		MoreGridIcon,
		ProjectorsIcon,
		VideoIcon,
		WandIcon,
	} from "luxedo-icons"
	import { Link } from "svelte-routing"
	import { navigateTo, NavigationStore } from "../../../stores/NavigationContext"
	import MobileOtherLinks from "./mobile-other-links/MobileOtherLinks.svelte"
	import { MobileOtherLinksController } from "./mobile-other-links/MobileOtherLinksController.svelte"
	import { AccountNavigator } from "../account/AccountStore"
	import { openWhatsNewOverlay } from "../../reusable/overlays/whats-new"
	import { showContentSuggestions } from "luxedo-comps"
	import { UserContext } from "../../../modules/UserContext.svelte"

	interface Props {
		children: Snippet
	}

	let { children }: Props = $props()

	let activeRoute: keyof typeof ROUTES | string = $state()
	NavigationStore.subscribe((route) => (activeRoute = route))

	const icons: { [key in keyof typeof ROUTES]: Component } = {
		"lux-ai": WandIcon,
		"media-library": ImageIcon,
		"show-library": VideoIcon,
		projector: ProjectorsIcon,
		schedule: CalendarIcon,
	}

	const contentLinksController = new MobileOtherLinksController({
		Media: () => navigateTo("media-library"),
		Shows: () => navigateTo("show-library"),
	})
	//

	function openContentOptions(e: MouseEvent) {
		e.preventDefault()
		contentLinksController.open()
	}

	const otherLinksController = new MobileOtherLinksController({
		Account: AccountNavigator.open,
		"Help Desk": "https://luxedo.com/pages/help",
		"What's New": openWhatsNewOverlay,
		"Content Solutions": showContentSuggestions,
	})

	function openOtherOptions(e: MouseEvent) {
		e.preventDefault()
		otherLinksController.open()
	}
</script>

{#snippet linkButton(Icon: Component, name: string, route: keyof typeof ROUTES)}
	<div
		class="nav-button {activeRoute === route ? 'active' : ''}"
		class:new={NEW_ROUTES.includes(route)}
		onclick={() => navigateTo(route)}
	>
		<Link to={route}>
			<Icon />
			{name}
		</Link>
	</div>
{/snippet}

<div class="mobile-navigator">
	{@render children()}

	<div class="button-bar">
		{@render linkButton(icons["lux-ai"], "LuxStudio", "lux-ai")}
		<div class="nav-button" class:active={["media-library", "show-library"].includes(activeRoute)}>
			<a href="#" onclick={openContentOptions} class="mobile-other-button">
				<VideoIcon />
				My Content
			</a>
			<MobileOtherLinks controller={contentLinksController} />
		</div>
		{@render linkButton(icons["schedule"], "Schedule", "schedule")}
		{@render linkButton(icons["projector"], "Projectors", "projector")}

		<div class="nav-button">
			<a href="#" onclick={openOtherOptions} class="mobile-other-button">
				<MoreGridIcon />
				More
			</a>
			<MobileOtherLinks controller={otherLinksController} />
		</div>
	</div>
</div>

<style>
	.mobile-navigator {
		display: flex;
		height: 100%;
		overflow: hidden;
		flex-direction: column;
	}

	.button-bar {
		padding: 0.25rem 0;
		background-color: var(--color-bg);
		display: flex;
		flex-direction: row;
		justify-content: space-evenly;
	}

	.nav-button {
		--nav-button-color: var(--color-text-light);
		padding: 0.25rem;
		position: relative;
	}

	.nav-button.active {
		--nav-button-color: var(--color-main);
	}

	.mobile-navigator > :global(:nth-child(1)) {
		flex-grow: 1;
		overflow: hidden;
	}

	.nav-button > :global(a) {
		color: var(--nav-button-color);
		border: none;
		display: flex;
		flex-direction: column;
		align-items: center;
		font-size: var(--h3);
	}

	.nav-button :global(svg) {
		max-height: 2rem;
	}

	.nav-button :global(.svg-fill) {
		fill: var(--nav-button-color);
	}

	.nav-button :global(.svg-stroke) {
		stroke: var(--nav-button-color);
	}
</style>
